import { ReactNode } from "react";

import i18n from "../../i18n";

import { UserDto } from "../../DTOs/User/UserDto";
import { AreaKey } from "../../utilities/helpers/pageVocabulary.generated";
import { hasArea, hasTile } from "../../utilities/helpers/roleUtilities";
import { CardSize, PlacedCard } from "./dashboardLayout";
import { DrillBlastTodayCard } from "./DashboardCards/DrillBlastTodayCard";
import { ExplosivesStockCard } from "./DashboardCards/ExplosivesStockCard";
import { HourlyMetricsCard } from "./DashboardCards/HourlyMetricsCard";
import { ImportChecksCard } from "./DashboardCards/ImportChecksCard";
import { LazyMachinePositionsCard } from "./DashboardCards/LazyMachinePositionsCard";
import { MaterialMovementCard } from "./DashboardCards/MaterialMovementCard";
import { PlantStatusCard } from "./DashboardCards/PlantStatusCard";
import { TargetsCard } from "./DashboardCards/TargetsCard";

export type DeploymentFeatures = { telemetryEnabled: boolean; drillBlastModules: string[] };

export interface DashboardCardDefinition {
  key: string;
  title: string;
  linkTo: string;
  linkTooltip: string;
  linkArea: AreaKey;
  defaultSize: CardSize;
  isVisible?: (user: UserDto | null, features: DeploymentFeatures) => boolean;
  content: ReactNode;
}

export const DASHBOARD_CARDS: DashboardCardDefinition[] = [
  {
    key: "material-movement",
    get title() {
      return i18n.t("cards.material-movement.title", { ns: "dashboard" });
    },
    linkTo: "/production/show",
    get linkTooltip() {
      return i18n.t("cards.material-movement.link", { ns: "dashboard" });
    },
    linkArea: "Production",
    defaultSize: { w: 6, h: "medium" },
    content: <MaterialMovementCard />,
  },
  {
    key: "plant-status",
    get title() {
      return i18n.t("cards.plant-status.title", { ns: "dashboard" });
    },
    linkTo: "/stoppages",
    get linkTooltip() {
      return i18n.t("cards.plant-status.link", { ns: "dashboard" });
    },
    linkArea: "Stoppages",
    defaultSize: { w: 6, h: "medium" },
    content: <PlantStatusCard />,
  },
  {
    key: "targets",
    get title() {
      return i18n.t("cards.targets.title", { ns: "dashboard" });
    },
    linkTo: "/admin/references/target-refs/targets",
    get linkTooltip() {
      return i18n.t("cards.targets.link", { ns: "dashboard" });
    },
    linkArea: "Targets",
    defaultSize: { w: 6, h: "medium" },
    content: <TargetsCard />,
  },
  {
    key: "import-checks",
    get title() {
      return i18n.t("cards.import-checks.title", { ns: "dashboard" });
    },
    linkTo: "/operations",
    get linkTooltip() {
      return i18n.t("cards.import-checks.link", { ns: "dashboard" });
    },
    linkArea: "Operations",
    defaultSize: { w: 6, h: "medium" },
    content: <ImportChecksCard />,
  },
  {
    key: "machine-positions",
    get title() {
      return i18n.t("cards.machine-positions.title", { ns: "dashboard" });
    },
    linkTo: "/telemetry/machine-positions",
    get linkTooltip() {
      return i18n.t("cards.machine-positions.link", { ns: "dashboard" });
    },
    linkArea: "MachinePositions",
    defaultSize: { w: 6, h: "medium" },
    isVisible: (user, { telemetryEnabled }) => telemetryEnabled && hasArea(user, "MachinePositions"),
    content: <LazyMachinePositionsCard />,
  },
  {
    key: "drill-blast-today",
    get title() {
      return i18n.t("cards.drill-blast-today.title", { ns: "dashboard" });
    },
    linkTo: "/db/bh-drilling-list",
    get linkTooltip() {
      return i18n.t("cards.drill-blast-today.link", { ns: "dashboard" });
    },
    linkArea: "BlastHoleDrillingList",
    defaultSize: { w: 6, h: "short" },
    content: <DrillBlastTodayCard />,
  },
  {
    key: "explosives-stock",
    get title() {
      return i18n.t("cards.explosives-stock.title", { ns: "dashboard" });
    },
    linkTo: "/db/explosives",
    get linkTooltip() {
      return i18n.t("cards.explosives-stock.link", { ns: "dashboard" });
    },
    linkArea: "ExplosivesStore",
    defaultSize: { w: 6, h: "short" },
    isVisible: (user, { drillBlastModules }) => drillBlastModules.includes("ExplosivesStore") && hasArea(user, "ExplosivesStore"),
    content: <ExplosivesStockCard />,
  },
  {
    key: "hourly-metrics",
    get title() {
      return i18n.t("cards.hourly-metrics.title", { ns: "dashboard" });
    },
    linkTo: "/reports/daily",
    get linkTooltip() {
      return i18n.t("cards.hourly-metrics.link", { ns: "dashboard" });
    },
    linkArea: "DailyReport",
    defaultSize: { w: 12, h: "medium" },
    content: <HourlyMetricsCard />,
  },
];

export type DashboardPlacement = PlacedCard<DashboardCardDefinition>;

export const availableCards = (user: UserDto | null, features: DeploymentFeatures): DashboardCardDefinition[] =>
  DASHBOARD_CARDS.filter(card => hasTile(user, card.key) && (card.isVisible?.(user, features) ?? true));
